import React, { Fragment } from 'react';
import { connect } from 'react-redux';
import styled from 'styled-components';

import Paper from '@material-ui/core/Paper';
import Card from '@material-ui/core/Card';
import { CardText } from 'material-ui/Card';
import FlatButton from 'material-ui/FlatButton';
import Spacer from 'components/ui/Spacer';
import Drawer from 'components/ui/Drawer';
import { getTenant } from 'actions/landlordTenantsActions';

import TenantNotes from './TenantNotes';
import AssistancRequestDrawerWizard from './AssistanceRequestDrawerWizard';

import { AccentColor } from 'helpers/AccentColorHelper';
import { deepDiver } from 'helpers/utilities';
import { toFriendlyDateString, formatPhone, formatMoney } from 'helpers/Formatter';
import StatusTag from 'components/ui/StatusTag';

import TenantResidentVerbiageHelper from 'components/shared/TenantResidentVerbiageHelper';
import { TenantAutopayStatus, PortalAccessibilityFlag } from 'components/shared/StatusHelper';
import Header from 'components/shared/Header';
import PropertyIcon from '@material-ui/icons/DomainTwoTone';
import UnitIcon from '@material-ui/icons/MeetingRoomTwoTone';
import { browserHistory } from 'react-router';
import { hostIsCapreit, hostIsBoardwalk } from 'helpers/CurrentWindowLocation';
import { colors } from 'helpers/ColorHelper';
import { getTranslatedString } from 'helpers/i18n';
import TenantOptInOptOut from './TenantOptInOptOut';
import debounce from 'lodash/debounce';
import { MdWarning as WarningIcon } from 'react-icons/md';
import { featureEnabled } from 'helpers/ToggleBot';
import { toFriendlyDateTimeString } from 'helpers/Formatter';
import { getCurrentLocale } from 'helpers/i18n';

let style = {
  avatar: {
    height: '130px',
    width: '130px',
    border: '1px solid #e1e1e1',
    backgroundSize: 'cover',
    float: 'right',
    backgroundRepeat: 'no-repeat',
    backgroundPosition: '50% 0',
  },
  transactionCard: {
    color: '#424242 !important',
    fontFamily: '"Circular", sans-serif !important',
    height: '100%',
    overflow: 'hidden',
    borderRadius: '8px',
    marginBottom: '15px',
  },
};

const StyledTenantDetailsContainer = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: flex-start;
  padding: 15px 17px 0px;
`;

const StyledEditButtonsContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 15px;
`;

const AssistanceRequestMessage = styled.div`
  padding: 15px;
  border-radius: 10px;
  background-color: ${colors.LIGHT_GRAY};
`

const LastModified = styled.div`
  font-size: 12px;
`;

const StyledMessage = styled.pre`
  font-family: 'Circular';
  background: none;
  border: none;
`;

const BOUNCE_WAIT_IN_MS = 1000;
const debouncedHandleClick = debounce(
  (onClick, ...params) => {
    if (onClick) {
      onClick(...params);
    }
  },
  BOUNCE_WAIT_IN_MS,
  { leading: true, trailing: false }
);

class TenantDetails extends React.Component {
  constructor(props) {
    super(props);
    if (props.tenantDetails.is_guarantor) {
      this.tentantVerbiage = getTranslatedString('landlordPortal.tenantDetails.guarantor');
    } else {
      this.tentantVerbiage = <TenantResidentVerbiageHelper uppercase />;
      this.tentantVerbiageExternalID = <TenantResidentVerbiageHelper capitalize />;
    }
    this.state = {
      assistanceRequestDrawerOpen: false
    }
  }

  render() {
    return (
      <React.Fragment>
        <div className="row">
          {this._renderBouncedEmail()}
          {this._renderInviteTenant()}
          {this._renderApplicants()}
        </div>
      </React.Fragment>
    );
  }

  _renderInviteButton = () => {
    const { tenantDetails, inviteTenant } = this.props;
    if (!tenantDetails.email) return;
    if (tenantDetails.bounced_email) return;

    let actionVerbiage;
    if (tenantDetails.status === 'invited' || tenantDetails.status === 'inviting') {
      actionVerbiage = `${getTranslatedString('landlordPortal.tenantDetails.remind')}`;
    } else if (tenantDetails.status === 'not_invited' || tenantDetails.status === 'opted_out') {
      actionVerbiage = `${getTranslatedString('landlordPortal.tenantDetails.invite')}`;
    }

    const label = <span>{actionVerbiage}</span>;

    return (
      <FlatButton
        label={label}
        primary
        backgroundColor={AccentColor()}
        hoverColor="grey"
        style={{
          color: 'white',
          marginTop: '5px',
          marginBottom: '25px',
        }}
        onClick={() => debouncedHandleClick(inviteTenant)}
      />
    );
  };

  _renderBouncedEmail = () => {
    let bouncedEmailVerbiage = null;
    if (featureEnabled('bounce_emails') && this.props.tenantDetails.bounced_email) {
      bouncedEmailVerbiage = (
        <div style={{ paddingTop: '10px', paddingBottom: '15px' }}>
          <WarningIcon
            size={14}
            style={{
              marginBottom: '2px',
              marginRight: '5px',
              verticalAlign: 'text-bottom',
              color: colors.PUMPKIN_ORANGE,
            }}
          />
          {getTranslatedString('landlordPortal.tenantDetails.bouncedEmail', {
            name: `${this.props.tenantDetails.first_name}`,
          })}
        </div>
      );

      return (
        <div className="row">
          <p style={{ marginBottom: '5px' }}>{bouncedEmailVerbiage}</p>
        </div>
      );
    }
  };

  _renderInviteTenant = () => {
    if (this.props.tenantDetails.invitation_accepted_at) return;
    if (!this.props.tenantDetails.portal_active) return;

    if (
      this.props.tenantDetails.status === 'active' ||
      this.props.tenantDetails.status === 'inactive' ||
      this.props.tenantDetails.status === 'opted_out' ||
      this.props.tenantDetails.bounced_email
    ) {
      return;
    }
    if (this.props.hideInvite) return;
    let inviteDetailsVerbage;

    const status = {
      invited: getTranslatedString('landlordPortal.tenantDetails.invited', {
        name: `${this.props.tenantDetails.first_name}`,
        date: `${toFriendlyDateString(this.props.tenantDetails.invitation_sent_at, {
          language: this.props.languageCode,
        })}`,
      }),
      inviting: getTranslatedString('landlordPortal.tenantDetails.inviting', {
        name: `${this.props.tenantDetails.first_name}`,
      }),
      not_invited: getTranslatedString('landlordPortal.tenantDetails.notInvited', {
        name: `${this.props.tenantDetails.first_name}`,
      }),
      opted_out: getTranslatedString('landlordPortal.tenantDetails.optedOut', {
        name: `${this.props.tenantDetails.first_name}`,
      }),
    };
    inviteDetailsVerbage = status[this.props.tenantDetails.status];
    if (!this.props.tenantDetails.email) {
      inviteDetailsVerbage = getTranslatedString(
        'landlordPortal.tenantDetails.inviteDetailsVerbiage',
        { name: `${this.props.tenantDetails.first_name}` }
      );
    }
    return (
      <div className="row">
        <p style={{ marginBottom: '5px' }}>{inviteDetailsVerbage}</p>
        <div className="row" style={{ textAlign: 'center' }}>
          {this._renderInviteButton()}
        </div>
      </div>
    );
  };

  _renderApplicants = () => {
    const tenant = this.props.tenantDetails;
    const disabledProfilePicture = this.props.disabledProfilePicture;
    let headerVerbiage;
    if (this.props.isProspect) {
      headerVerbiage = getTranslatedString('landlordPortal.tenantDetails.prospectDetails');
    } else if (tenant.is_guarantor) {
      headerVerbiage = getTranslatedString('landlordPortal.tenantDetails.guarantorDetails');
    } else {
      headerVerbiage = (
        <span style={{ textTransform: 'uppercase !important' }}>
          {this.tentantVerbiage} {getTranslatedString('landlordPortal.tenantDetails.detailsUpcase')}
        </span>
      );
    }

    style = {
      ...style,
      avatar: {
        ...style.avatar,
        backgroundImage: 'url(' + deepDiver(tenant, 'avatar_url') + ')',
      },
    };

    const canAddNotes = !hostIsBoardwalk();

    // TODO: Remove this
    const isBudgetRangeSet =
      tenant.min_budget &&
      tenant.max_budget &&
      ![tenant.min_budget, tenant.max_budget].includes('$0.00');

    let optInOptOut = 'opt_out';
    if (tenant.status === 'opted_out') {
      optInOptOut = 'opt_in';
    }
    return (
      <Card style={style.transactionCard}>
        <CardText style={{ padding: '0' }}>
          <Header name={headerVerbiage} />
          <StyledTenantDetailsContainer>
            {!disabledProfilePicture && (
              <div>
                <Paper style={style.avatar} elevation={0} variant="outlined" />
              </div>
            )}
            <div style={{ marginLeft: '15px' }}>
              <div style={{ marginBottom: '15px' }}>
                {this._renderAutopayStatus()}
                {this._renderProspectStatus()}

                <PortalAccessibilityFlag
                  portalAccess={tenant.can_access_portal}
                  isAgentYuhu={this.props.isAgentYuhu}
                />
              </div>
              <h4 style={{ fontFamily: 'Circular-Bold' }}>{tenant.full_name}</h4>
              <h5 style={{ overflowWrap: 'break-word' }}>{tenant.email}</h5>
              <h5 style={{ marginBottom: '15px' }}>{formatPhone(tenant.phone_number)}</h5>
              {tenant.external_id && (
                <h5 style={{ margin: '0' }}>
                  {this.tentantVerbiageExternalID}{' '}
                  {getTranslatedString('landlordPortal.tenantDetails.externalIDCode', {
                    external_id: tenant.external_id,
                  })}
                </h5>
              )}
              {this.props.isProspect && ( // TODO: Redo this part
                <h5 style={{ marginTop: 0 }}>
                  {getTranslatedString('landlordPortal.tenantDetails.preferredBudget')}:<br />
                  {isBudgetRangeSet
                    ? `${formatMoney(tenant.min_budget)} to ${formatMoney(tenant.max_budget)}`
                    : getTranslatedString('landlordPortal.tenantDetails.noPreference')}
                </h5>
              )}
            </div>
          </StyledTenantDetailsContainer>
          {this._renderEditButton()}
          {this._renderLeaseSection()}
          {this._renderAssistanceRequest()}
          {this._renderAssistanceRequestUpdateDrawer()}
          {this.props.showLeaseSection &&
            !this.props.isProspect &&
            tenant.status !== 'inactive' && (
              <TenantOptInOptOut
                optOutLead={this.props.optOutLead}
                optInLead={this.props.optInLead}
                optInOptOut={optInOptOut}
                tenantDetails={this.props.tenantDetails}
              />
            )}
          {canAddNotes && <TenantNotes leadID={this.props.leadID} />}
        </CardText>
      </Card>
    );
  };

  _renderAutopayStatus = () => {
    if (this.props.isProspect) return;
    if (hostIsCapreit()) return;

    return (
      <TenantAutopayStatus style={{ fontSize: '90%' }} tenantDetails={this.props.tenantDetails} />
    );
  };

  _renderProspectStatus = () => {
    if (!this.props.isProspect) return;

    return <StatusTag status={this.props.tenantDetails.humanized_status} />;
  };

  _renderEditButton = () => {
    if (this.props.hideEdit) return;

    return (
      <StyledEditButtonsContainer>
        <FlatButton
          label={
            this.props.editButtonLabelText || (
              <span>
                {getTranslatedString('landlordPortal.tenantDetails.edit')} {this.tentantVerbiage}
              </span>
            )
          }
          backgroundColor="white"
          hoverColor={colors.LIGHT_GRAY}
          style={{
            color: AccentColor(),
          }}
          onClick={this.props.toggleEditTenantDialog}
        />
        {this.props.showChatButton && (
          <FlatButton
            label={getTranslatedString('landlordPortal.tenantDetails.openChat')}
            primary
            backgroundColor={AccentColor()}
            hoverColor="grey"
            style={{
              color: 'white',
            }}
            onClick={this.props.goToChat}
          />
        )}
      </StyledEditButtonsContainer>
    );
  };

  _renderLeaseEffectiveEndDate = (moveOutDate, terminationDate, leaseDetails) => {
    if (moveOutDate) {
      return toFriendlyDateString(moveOutDate, {
        language: this.props.languageCode,
      });
    }

    if (terminationDate) {
      return toFriendlyDateString(terminationDate, {
        language: this.props.languageCode,
      });
    }

    if (hostIsCapreit()) return null;

    return toFriendlyDateString(leaseDetails.lease_end_date, {
      language: this.props.languageCode,
    });
  };

  _renderLeaseEffectiveStartDate = (moveInDate, leaseDetails) => {
    if (moveInDate) {
      return toFriendlyDateString(moveInDate, {
        language: this.props.languageCode,
      });
    }

    if (leaseDetails.lease_start_date) {
      return toFriendlyDateString(leaseDetails.lease_start_date, {
        language: this.props.languageCode,
      });
    }
  };

  toggleAssistanceRequestDrawer = () => {
    this.setState(prevState => ({ assistanceRequestDrawerOpen: !prevState.assistanceRequestDrawerOpen }));
  }

  _renderAssistanceRequest = () => {
    const { 
      request_assistance_enabled,
      assistance_request_updated_at, 
      assistance_request_updated_by, 
      requires_assistance, 
      assistance_request
    } = this.props.tenantDetails;
    
    if (!request_assistance_enabled) return null;

    let request = getTranslatedString('landlordPortal.residents.assistanceRequest.noRequest');
    if (requires_assistance) {
      request = (
        <AssistanceRequestMessage>
          <LastModified>
            {
              getTranslatedString(
                'landlordPortal.residents.assistanceRequest.lastUpdatedOn',
                { 
                  time: toFriendlyDateTimeString(assistance_request_updated_at, { language: getCurrentLocale() }),
                  updater: getTranslatedString(`landlordPortal.residents.assistanceRequest.${assistance_request_updated_by}`)
                }
              )
            }
          </LastModified>
          <Spacer mt={1}>
            <StyledMessage>{assistance_request}</StyledMessage>
          </Spacer>
        </AssistanceRequestMessage>
      );
    }
    return (
      <div>
        <Header name={getTranslatedString('landlordPortal.residents.assistanceRequest.header')} />
        <Spacer mt={1} mb={1} pr={1} pl={1}>
          {getTranslatedString('landlordPortal.residents.assistanceRequest.description')}
          <Spacer mt={1}>{request}</Spacer>
          <Spacer mt={1}>
            <FlatButton
              label={getTranslatedString('landlordPortal.residents.assistanceRequest.updateRequest')}
              backgroundColor={AccentColor()}
              hoverColor="grey"
              style={{ color: 'white' }}
              onClick={this.toggleAssistanceRequestDrawer}
            />
          </Spacer>
        </Spacer>
      </div>
    );
  }

  _renderAssistanceRequestUpdateDrawer = () => {
    return (
      <Drawer
        visible={this.state.assistanceRequestDrawerOpen}
        onClose={this.toggleAssistanceRequestDrawer}
        closable={false}
        width={550}
      >
        {this.state.assistanceRequestDrawerOpen && (
          <AssistancRequestDrawerWizard
            cotenantId={this.props.tenantDetails.id}
            getTenant={this.props.getTenant}
            assistanceRequst={this.props.tenantDetails.assistance_request}
            requiresAssistance={this.props.tenantDetails.requires_assistance}
            handleClose={this.toggleAssistanceRequestDrawer}
          />
        )}
      </Drawer>
    );
  }

  _renderLeaseSection = () => {
    if (!this.props.showLeaseSection) return;
    const leaseDetails = this.props.tenantDetails.lease;

    if (!leaseDetails) return null;

    return (
      <div>
        <Header name={getTranslatedString('landlordPortal.tenantDetails.leaseDetailsUpcase')} />
        <div className="row" style={{ marginBottom: '15px', marginTop: '15px' }}>
          <div className="col-xs-12">
            <span style={{ fontSize: '17px', fontFamily: 'Circular-Bold' }}>
              <span>
                <PropertyIcon
                  size={16}
                  style={{ verticalAlign: 'text-bottom', color: AccentColor() }}
                />
              </span>
              &nbsp;{leaseDetails.property_name}
            </span>
          </div>
          <div className="col-xs-12">
            <span style={{ fontSize: '17px', fontFamily: 'Circular-Bold' }}>
              <span>
                <UnitIcon
                  size={16}
                  style={{ verticalAlign: 'text-bottom', color: AccentColor() }}
                />
              </span>
              &nbsp;{getTranslatedString('landlordPortal.tenantDetails.unit')}{' '}
              {leaseDetails.unit_number}
            </span>
          </div>
        </div>
        <div className="row" style={{ marginBottom: '15px' }}>
          <div className="col-xs-6">
            {getTranslatedString('landlordPortal.tenantDetails.startDate')}
            <div style={{ fontSize: '17px', fontFamily: 'Circular-Bold' }}>
              {this._renderLeaseEffectiveStartDate(
                this.props.tenantDetails.move_in_date,
                leaseDetails
              )}
            </div>
          </div>
          <div className="col-xs-6" style={{ textAlign: 'right' }}>
            {getTranslatedString('landlordPortal.tenantDetails.endDate')}
            <div style={{ fontSize: '17px', fontFamily: 'Circular-Bold' }}>
              {this._renderLeaseEffectiveEndDate(
                this.props.tenantDetails.move_out_date,
                this.props.tenantDetails.termination_date,
                leaseDetails
              )}
            </div>
          </div>
        </div>
        <div className="row" style={{ marginBottom: '15px' }}>
          {this._renderExternalLeaseId(leaseDetails)}
        </div>
        <div className="row" style={{ marginBottom: '15px' }}>
          {this._renderViewLeaseLink(leaseDetails)}
        </div>
        <div className="row" style={{ marginBottom: '15px' }}>
          {this._renderOtherLeaseHolders()}
        </div>
      </div>
    );
  };

  _renderExternalLeaseId = (leaseDetails) => {
    if (!leaseDetails.external_lease_id) return;
    return (
      <Fragment>
        <div className="col-xs-12">
          {getTranslatedString('landlordPortal.tenantDetails.leaseId')}
          <div style={{ fontSize: '17px', fontFamily: 'Circular-Bold' }}>
            {leaseDetails.external_lease_id}
          </div>
        </div>
      </Fragment>
    );
  };

  _renderViewLeaseLink = (leaseDetails) => {
    if (hostIsCapreit() || hostIsBoardwalk()) return;
    return (
      <div className="col-xs-12">
        <button
          type="button"
          className="link-button"
          onClick={() => browserHistory.push(`/companies/rent_roll/${leaseDetails.id}`)}
        >
          {getTranslatedString('landlordPortal.tenantDetails.viewLease')}
        </button>
      </div>
    );
  };

  _renderOtherLeaseHolders = () => {
    if (this.props.tenantDetails.other_lease_holders.length === 0) return;

    return (
      <Fragment>
        <div className="col-xs-12">
          <h5
            style={{
              paddingBottom: '5px',
              margin: '0 0 5px',
              borderBottom: '2px solid ' + AccentColor(),
            }}
          >
            {getTranslatedString('landlordPortal.tenantDetails.otherLeaseHolders')}
          </h5>
        </div>
        {this.props.tenantDetails.other_lease_holders.map((lease_holder) => {
          return (
            <div key={lease_holder.id} className="col-xs-12">
              <button
                type="button"
                className="link-button"
                onClick={() => this.props.openAnotherTenant(lease_holder.id)}
              >
                {lease_holder.full_name}
              </button>
              {lease_holder.is_guarantor && (
                <span>
                  {' '}
                  {getTranslatedString('landlordPortal.tenantDetails.guarantorBrackets')}{' '}
                </span>
              )}
              {lease_holder.status === 'inactive' && (
                <span>
                  {' '}
                  {getTranslatedString('landlordPortal.tenantDetails.inactiveBrackets')}{' '}
                </span>
              )}
              {lease_holder.status === 'inactive' && (
                <span>
                  {' '}
                  {getTranslatedString('landlordPortal.tenantDetails.inactiveBrackets')}{' '}
                </span>
              )}
            </div>
          );
        })}
      </Fragment>
    );
  };
}

export default connect((store) => {
  return {
    isAgentYuhu: store.userTypes.isYuhu,
    disabledProfilePicture:
      store.companyDetails.companyPreferences.disable_residents_profile_picture,
  };
}, { getTenant })(TenantDetails);
